import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import {
  UserSeasonPass,
  ClaimRewardsResponse,
  SeasonPassProgress,
  SeasonId,
  ClaimNodeState
} from '../types/seasonPass';
import {
  DEFAULT_SEASON
} from '../data/seasonPassData';
import { getSeasonNodesByCurriculum, getNodeByIdByCurriculum } from '../data/seasonPassRegistry';

/**
 * Service for managing Season Pass functionality
 * Handles user progress, claiming rewards, and game unlocking
 */
export class SeasonPassService {

  // Field helpers to scope by squad and curriculum with backward compatibility
  private static spField(seasonId: SeasonId, squadId?: string, curriculumId?: string) {
    if (squadId && curriculumId) return `seasonPassBySquadCurriculum.${squadId}.${curriculumId}.${seasonId}`;
    return `seasonPass.${seasonId}`; // legacy
  }

  /**
   * Public helper: merge-add unlocked games for a user (scoped if provided)
   */
  static async addUnlockedGames(
    userId: string,
    newGameIds: string[],
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<void> {
    const current = await this.getUnlockedGames(userId, DEFAULT_SEASON, options);
    const merged = Array.from(new Set([...(current || []), ...newGameIds]));
    await this.updateUnlockedGames(userId, merged, options);
  }

  /**
   * Bulk helper: add unlocked games for multiple users
   */
  static async addUnlockedGamesForUsers(
    userIds: string[],
    gameIds: string[],
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<void> {
    for (const uid of userIds) {
      await this.addUnlockedGames(uid, gameIds, options);
    }
  }

  private static unlockedGamesField(squadId?: string, curriculumId?: string) {
    if (squadId && curriculumId) return `unlockedGamesBySquadCurriculum.${squadId}.${curriculumId}`;
    return 'unlockedGames'; // legacy
  }

  private static getSeasonNodes(curriculumId: string, seasonId: SeasonId) {
    return getSeasonNodesByCurriculum(curriculumId, seasonId);
  }

  /**
   * Initialize a user's season pass if it doesn't exist
   * Uses existing reflectionGame collection to avoid permission issues
   */
  static async initializeUserSeasonPass(
    userId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<UserSeasonPass> {
    try {
      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');
      let reflectionGameDoc = await getDoc(reflectionGameRef);

      if (!reflectionGameDoc.exists()) {
        console.log('[SeasonPassService] Creating new reflection game state for user:', userId);
        // Create a new reflection game state document for new users
        const initialData = {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        // Use setDoc to create the document
        await setDoc(reflectionGameRef, initialData);

        // Re-fetch the document
        reflectionGameDoc = await getDoc(reflectionGameRef);
      }

      const reflectionData = reflectionGameDoc.data();
      const { squadId, curriculumId } = options || {};
      const existingSeasonPass = squadId && curriculumId
        ? reflectionData?.seasonPassBySquadCurriculum?.[squadId]?.[curriculumId]?.[seasonId]
        : reflectionData?.seasonPass?.[seasonId];

      if (existingSeasonPass) {
        return existingSeasonPass as UserSeasonPass;
      }

      // Check if user has existing unlocked games that we should preserve
      const existingUnlockedGames = squadId && curriculumId
        ? (reflectionData?.unlockedGamesBySquadCurriculum?.[squadId]?.[curriculumId] || [])
        : (reflectionData?.unlockedGames || []);

      // Create new season pass with only start node unlocked
      const initialSeasonPass: UserSeasonPass = {
        seasonId,
        unlockedNodes: ['start_node'], // Only start node unlocked initially
        claimedRewards: [],
        claimedNodes: [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      console.log('[SeasonPassService] Initializing user season pass:', initialSeasonPass);
      console.log('[SeasonPassService] Preserving existing unlocked games:', existingUnlockedGames);

      // Store in reflectionGame collection under seasonPass field
      // Use a transaction to ensure atomicity and preserve existing unlocked games
      await runTransaction(db, async (transaction) => {
        // Re-read the document within the transaction to ensure consistency
        const currentDoc = await transaction.get(reflectionGameRef);
        const currentData = currentDoc.data();

        // Only proceed if no season pass exists for this season
        const hasSP = squadId && curriculumId
          ? currentData?.seasonPassBySquadCurriculum?.[squadId]?.[curriculumId]?.[seasonId]
          : currentData?.seasonPass?.[seasonId];
        if (!hasSP) {
          const updates: any = {
            [this.spField(seasonId, squadId, curriculumId)]: {
              ...initialSeasonPass,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            }
          };

          // If there are existing unlocked games but no season pass, preserve them
          const existingUG = squadId && curriculumId
            ? currentData?.unlockedGamesBySquadCurriculum?.[squadId]?.[curriculumId]
            : currentData?.unlockedGames;
          if (existingUG && existingUG.length > 0) {
            updates[this.unlockedGamesField(squadId, curriculumId)] = existingUG;
            updates.unlockedGamesLastUpdated = serverTimestamp();
          } else if (existingUnlockedGames.length === 0) {
            // Only set default games if truly no games exist
            const resolvedCurriculum = curriculumId || 'music';
            const startNodeGames = await this.getStartNodeGames(seasonId, resolvedCurriculum);
            // Ensure Overture Music Lab is unlocked for everyone by default
            const defaultUnlocked = Array.from(new Set([...
              startNodeGames,
              'overture-music-lab'
            ]));
            updates[this.unlockedGamesField(squadId, curriculumId)] = defaultUnlocked;
            updates.unlockedGamesLastUpdated = serverTimestamp();
          }

          transaction.update(reflectionGameRef, updates);
        }
      });

      return initialSeasonPass;
    } catch (error) {
      console.error('Error initializing user season pass:', error);
      throw new Error('Failed to initialize season pass');
    }
  }

  /**
   * Get user's current season pass state
   */
  static async getUserSeasonPass(
    userId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<UserSeasonPass> {
    try {
      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');
      const reflectionGameDoc = await getDoc(reflectionGameRef);

      if (!reflectionGameDoc.exists()) {
        return await this.initializeUserSeasonPass(userId, seasonId, options);
      }

      const reflectionData = reflectionGameDoc.data();
      const { squadId, curriculumId } = options || {};
      const seasonPassData = squadId && curriculumId
        ? reflectionData?.seasonPassBySquadCurriculum?.[squadId]?.[curriculumId]?.[seasonId]
        : reflectionData?.seasonPass?.[seasonId];

      if (!seasonPassData) {
        return await this.initializeUserSeasonPass(userId, seasonId, options);
      }

      return seasonPassData as UserSeasonPass;
    } catch (error) {
      console.error('Error getting user season pass:', error);
      throw new Error('Failed to get season pass data');
    }
  }

  /**
   * Get user's unlocked games state
   */
  static async getUnlockedGames(userId: string, seasonId: SeasonId = DEFAULT_SEASON, options?: { squadId?: string; curriculumId?: string }): Promise<string[]> {
    try {
      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');
      const reflectionGameDoc = await getDoc(reflectionGameRef);

      if (!reflectionGameDoc.exists()) {
        return [];
      }

      const reflectionData = reflectionGameDoc.data();
      const { squadId, curriculumId } = options || {};
      let unlockedGames = squadId && curriculumId
        ? reflectionData?.unlockedGamesBySquadCurriculum?.[squadId]?.[curriculumId]
        : reflectionData?.unlockedGames;

      // If no specific squad/curriculum is requested (Games page), return a UNION of
      // the legacy list and ALL scoped entries so the collection view is complete.
      if (!squadId || !curriculumId) {
        const unionSet = new Set<string>(Array.isArray(reflectionData?.unlockedGames) ? reflectionData.unlockedGames : []);
        const bySquad = reflectionData?.unlockedGamesBySquadCurriculum as Record<string, Record<string, string[]>> | undefined;
        if (bySquad && typeof bySquad === 'object') {
          for (const sId of Object.keys(bySquad)) {
            const byCurr = bySquad[sId] || {};
            for (const cId of Object.keys(byCurr)) {
              const arr = byCurr[cId] || [];
              for (const g of arr) unionSet.add(g);
            }
          }
        }
        unlockedGames = Array.from(unionSet);
      }

      if (!unlockedGames || unlockedGames.length === 0) {
        // Fallbacks to handle legacy vs scoped storage mismatches gracefully
        // If options were provided (scoped read), fallback to legacy list before declaring empty
        if (squadId && curriculumId && Array.isArray(reflectionData?.unlockedGames) && reflectionData.unlockedGames.length > 0) {
          unlockedGames = reflectionData.unlockedGames;
        }

        // If still empty, only initialize if there is no season pass progress
        if (!unlockedGames || unlockedGames.length === 0) {
          const hasSeasonPassData = reflectionData?.seasonPass &&
            Object.keys(reflectionData.seasonPass).some(seasonId =>
              reflectionData.seasonPass[seasonId]?.claimedRewards?.length > 0 ||
              reflectionData.seasonPass[seasonId]?.claimedNodes?.length > 0
            );

          if (!hasSeasonPassData) {
            const resolvedCurriculum = curriculumId || 'music';
            const startNodeGames = await this.getStartNodeGames(seasonId, resolvedCurriculum);
            await this.updateUnlockedGames(userId, startNodeGames, options);
            return startNodeGames;
          } else {
            console.warn('[SeasonPassService] User has season pass progress but no unlocked games - potential data inconsistency');
            return [];
          }
        }
      }

      // Always guarantee Overture Music Lab is present in unlocked list
      const ensured = Array.isArray(unlockedGames) ? unlockedGames : [];
      if (!ensured.includes('overture-music-lab')) {
        try {
          await this.updateUnlockedGames(userId, [...ensured, 'overture-music-lab'], options);
          ensured.push('overture-music-lab');
        } catch (e) {
          console.warn('Failed to ensure Overture Music Lab unlocked:', e);
        }
      }

      return ensured;
    } catch (error) {
      console.error('Error getting unlocked games:', error);
      return [];
    }
  }

  /**
   * Get games from start node that should be unlocked by default
   */
  private static async getStartNodeGames(seasonId: SeasonId = DEFAULT_SEASON, curriculumId: string = 'music'): Promise<string[]> {
    const preLevel1Node = getNodeByIdByCurriculum(curriculumId, 'pre_level_1', seasonId);

    const games: string[] = [];

    // Only get games from pre_level_1 since start node is now empty
    if (preLevel1Node) {
      games.push(...preLevel1Node.rewards.map(reward => reward.gameId));
    }

    return games;
  }

  /**
   * Update user's unlocked games
   */
  private static async updateUnlockedGames(userId: string, gameIds: string[], options?: { squadId?: string; curriculumId?: string }): Promise<void> {
    try {
      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');

      const updates: any = {
        unlockedGamesLastUpdated: serverTimestamp(),
        _lastUnlockContext: {
          squadId: options?.squadId || null,
          curriculumId: options?.curriculumId || null,
          updatedAt: serverTimestamp()
        }
      };
      const { squadId, curriculumId } = options || {};

      if (squadId && curriculumId) {
        // Build nested map so Firestore creates/merges the correct structure
        updates['unlockedGamesBySquadCurriculum'] = {
          [squadId]: {
            [curriculumId]: gameIds
          }
        };
      } else {
        // Legacy flat list (no context)
        updates['unlockedGames'] = gameIds;
      }

      // Use setDoc with merge to create doc if missing and deep-merge nested maps
      await setDoc(reflectionGameRef, updates, { merge: true });
    } catch (error) {
      console.error('Error updating unlocked games:', error);
      throw new Error('Failed to update unlocked games');
    }
  }

  /**
   * Check if a user can access a specific game
   */
  static async canUserPlayGame(
    userId: string,
    gameId: string,
    userRole?: string
  ): Promise<boolean> {
    try {
      // Teachers have access to all games
      if (userRole === 'teacher') {
        return true;
      }

      const unlockedGames = await this.getUnlockedGames(userId);
      return unlockedGames.includes(gameId);
    } catch (error) {
      console.error('Error checking game access:', error);
      return false;
    }
  }

  /**
   * Get the state of a specific claim node for a user
   */
  static async getNodeState(
    userId: string,
    nodeId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<ClaimNodeState> {
    try {
      const seasonPass = await this.getUserSeasonPass(userId, seasonId, options);
      const curriculumId = options?.curriculumId || 'music';
      const node = getNodeByIdByCurriculum(curriculumId, nodeId, seasonId);

      if (!node) {
        return 'locked';
      }

      // Check if node is fully claimed
      if (seasonPass.claimedNodes.includes(nodeId)) {
        return 'claimed';
      }

      // Check if node has always_unlocked requirement (like start_node)
      if (node.requirements.type === 'always_unlocked') {
        return 'claimable';
      }

      // Check if node has node_claimed requirement (like pre_level_1)
      if (node.requirements.type === 'node_claimed' && node.requirements.nodeId) {
        const requiredNodeIsClaimed = seasonPass.claimedNodes.includes(node.requirements.nodeId);
        return requiredNodeIsClaimed ? 'claimable' : 'locked';
      }

      // Check if node is unlocked (claimable)
      if (seasonPass.unlockedNodes.includes(nodeId)) {
        return 'claimable';
      }

      // Node is locked
      return 'locked';
    } catch (error) {
      console.error('Error getting node state:', error);
      return 'locked';
    }
  }

  /**
   * Unlock new nodes based on level completion
   */
  static async checkAndUnlockNodes(
    userId: string,
    completedLevelId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<string[]> {
    try {
      console.log('[SeasonPassService] Checking unlocks for level:', completedLevelId, 'user:', userId);

      const curriculumId = options?.curriculumId || 'music';
      const seasonNodes = this.getSeasonNodes(curriculumId, seasonId);
      const newlyUnlockedNodes: string[] = [];

      // Find nodes that should be unlocked by this level completion
      const nodesToUnlock = seasonNodes.filter(node =>
        node.requirements.type === 'level_complete' &&
        node.requirements.levelId === completedLevelId
      );

      console.log('[SeasonPassService] Found nodes to unlock:', nodesToUnlock.map(n => n.id));

      if (nodesToUnlock.length === 0) {
        return [];
      }

      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');

      await runTransaction(db, async (transaction) => {
        const reflectionGameDoc = await transaction.get(reflectionGameRef);

        if (!reflectionGameDoc.exists()) {
          throw new Error('Reflection game state not found');
        }

        const reflectionData = reflectionGameDoc.data();
        const { squadId } = options || {};
        const seasonPass = squadId && curriculumId
          ? reflectionData?.seasonPassBySquadCurriculum?.[squadId]?.[curriculumId]?.[seasonId]
          : reflectionData?.seasonPass?.[seasonId];

        if (!seasonPass) {
          throw new Error('Season pass not found');
        }

        const updatedUnlockedNodes = [...seasonPass.unlockedNodes];

        nodesToUnlock.forEach(node => {
          if (!updatedUnlockedNodes.includes(node.id)) {
            updatedUnlockedNodes.push(node.id);
            newlyUnlockedNodes.push(node.id);
          }
        });

        const updates: any = {};
        updates[`${this.spField(seasonId, options?.squadId, curriculumId)}.unlockedNodes`] = updatedUnlockedNodes;
        updates[`${this.spField(seasonId, options?.squadId, curriculumId)}.updatedAt`] = serverTimestamp();
        transaction.update(reflectionGameRef, updates);
      });

      return newlyUnlockedNodes;
    } catch (error) {
      console.error('Error checking and unlocking nodes:', error);
      return [];
    }
  }

  /**
   * Claim rewards from a specific node
   */
  static async claimNodeRewards(
    userId: string,
    nodeId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<ClaimRewardsResponse> {
    try {
      const curriculumId = options?.curriculumId || 'music';
      const node = getNodeByIdByCurriculum(curriculumId, nodeId, seasonId);

      if (!node) {
        return {
          success: false,
          claimedRewards: [],
          newlyUnlockedGames: [],
          error: 'Node not found'
        };
      }

      // Check if user can claim this node
      const nodeState = await this.getNodeState(userId, nodeId, seasonId, options);

      if (nodeState !== 'claimable') {
        return {
          success: false,
          claimedRewards: [],
          newlyUnlockedGames: [],
          error: 'Node is not claimable'
        };
      }

      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');

      const result = await runTransaction(db, async (transaction) => {
        // Get current state
        const reflectionGameDoc = await transaction.get(reflectionGameRef);

        if (!reflectionGameDoc.exists()) {
          throw new Error('Reflection game state not found');
        }

        const reflectionData = reflectionGameDoc.data();
        const { squadId, curriculumId } = options || {};
        const seasonPass = squadId && curriculumId
          ? reflectionData?.seasonPassBySquadCurriculum?.[squadId]?.[curriculumId]?.[seasonId]
          : reflectionData?.seasonPass?.[seasonId];

        if (!seasonPass) {
          throw new Error('Season pass not found');
        }

        const currentUnlockedGames = squadId && curriculumId
          ? (reflectionData?.unlockedGamesBySquadCurriculum?.[squadId]?.[curriculumId] || [])
          : (reflectionData?.unlockedGames || []);

        // Get new game IDs from node rewards
        const newGameIds = node.rewards
          .filter(reward => reward.type === 'game')
          .map(reward => reward.gameId);

        const newlyUnlockedGames = newGameIds.filter(gameId =>
          !currentUnlockedGames.includes(gameId)
        );

        const updatedUnlockedGames = [...currentUnlockedGames, ...newlyUnlockedGames];

        // Update season pass
        const updatedClaimedRewards = [
          ...seasonPass.claimedRewards,
          ...node.rewards.map(reward => reward.id)
        ];
        const updatedClaimedNodes = [...seasonPass.claimedNodes, nodeId];

        console.log(`[SeasonPassService] Claiming node ${nodeId}, updating claimedNodes from [${seasonPass.claimedNodes.join(', ')}] to [${updatedClaimedNodes.join(', ')}]`);

        const updates: any = {
          unlockedGamesLastUpdated: serverTimestamp()
        };
        updates[`${this.spField(seasonId, options?.squadId, options?.curriculumId)}.claimedRewards`] = updatedClaimedRewards;
        updates[`${this.spField(seasonId, options?.squadId, options?.curriculumId)}.claimedNodes`] = updatedClaimedNodes;
        updates[`${this.spField(seasonId, options?.squadId, options?.curriculumId)}.lastClaimedAt`] = serverTimestamp();
        updates[`${this.spField(seasonId, options?.squadId, options?.curriculumId)}.updatedAt`] = serverTimestamp();
        updates[this.unlockedGamesField(options?.squadId, options?.curriculumId)] = updatedUnlockedGames;
        transaction.update(reflectionGameRef, updates);
        
        return {
          claimedRewards: node.rewards,
          newlyUnlockedGames
        };
      });


      // Check if this claim unlocks the next node
      const nextUnlockedNode = await this.findNextUnlockableNode(userId, seasonId, options);

      return {
        success: true,
        claimedRewards: result.claimedRewards,
        newlyUnlockedGames: result.newlyUnlockedGames,
        nextUnlockedNode
      };
    } catch (error) {
      console.error('Error claiming node rewards:', error);
      return {
        success: false,
        claimedRewards: [],
        newlyUnlockedGames: [],
        error: 'Failed to claim rewards'
      };
    }
  }


  /**
   * Find the next node that could potentially be unlocked
   */
  private static async findNextUnlockableNode(
    userId: string,
    seasonId: SeasonId,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<string | undefined> {
    try {
      const seasonPass = await this.getUserSeasonPass(userId, seasonId, options);
      const curriculumId = options?.curriculumId || 'music';
      const seasonNodes = this.getSeasonNodes(curriculumId, seasonId);

      // Find nodes that are not yet unlocked but could be unlocked
      const potentialNodes = seasonNodes.filter(node =>
        !seasonPass.unlockedNodes.includes(node.id) &&
        !seasonPass.claimedNodes.includes(node.id)
      );

      // For now, return the first potential node
      // In the future, this could check level completion status
      return potentialNodes[0]?.id;
    } catch (error) {
      console.error('Error finding next unlockable node:', error);
      return undefined;
    }
  }

  /**
   * Get user's season pass progress statistics
   */
  static async getSeasonPassProgress(
    userId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<SeasonPassProgress> {
    try {
      const seasonPass = await this.getUserSeasonPass(userId, seasonId, options);
      const curriculumId = options?.curriculumId || 'music';
      const seasonNodes = this.getSeasonNodes(curriculumId, seasonId);

      const totalNodes = seasonNodes.length;
      const claimedNodes = seasonPass.claimedNodes.length;

      const totalRewards = seasonNodes.reduce((total, node) => total + node.rewards.length, 0);
      const claimedRewards = seasonPass.claimedRewards.length;

      const completionPercentage = totalNodes > 0 ? (claimedNodes / totalNodes) * 100 : 0;

      // Find current node (first unlocked but not claimed)
      let currentNodePosition = 0;
      for (const node of seasonNodes) {
        if (seasonPass.unlockedNodes.includes(node.id) &&
            !seasonPass.claimedNodes.includes(node.id)) {
          currentNodePosition = node.position;
          break;
        }
      }

      return {
        currentNode: currentNodePosition,
        totalNodes,
        claimedRewards,
        totalRewards,
        completionPercentage
      };
    } catch (error) {
      console.error('Error getting season pass progress:', error);
      return {
        currentNode: 0,
        totalNodes: 0,
        claimedRewards: 0,
        totalRewards: 0,
        completionPercentage: 0
      };
    }
  }

  /**
   * Get completed levels from reflection game state
   */
  static async getCompletedLevels(userId: string): Promise<string[]> {
    try {
      const reflectionGameDoc = await getDoc(doc(db, 'users', userId, 'reflectionGame', 'state'));

      if (!reflectionGameDoc.exists()) {
        return [];
      }

      const state = reflectionGameDoc.data() as any; // Using any to match existing pattern
      const completedLevels = state?.completedLevels || {};

      return Object.keys(completedLevels);
    } catch (error) {
      console.error('Error getting completed levels:', error);
      return [];
    }
  }

  /**
   * Initialize season pass based on current completed levels
   */
  static async initializeSeasonPassFromProgress(
    userId: string,
    seasonId: SeasonId = DEFAULT_SEASON,
    options?: { squadId?: string; curriculumId?: string }
  ): Promise<void> {
    try {
      console.log('[SeasonPassService] Initializing season pass from progress for user:', userId);

      // Get completed levels (legacy flat list)
      const completedLevels = await this.getCompletedLevels(userId);
      console.log('[SeasonPassService] Found completed levels:', completedLevels);

      // Initialize basic season pass (scoped if options provided)
      await this.initializeUserSeasonPass(userId, seasonId, options);

      // Get current season pass state (scoped if options provided)
      const seasonPass = await this.getUserSeasonPass(userId, seasonId, options);
      const curriculumId = options?.curriculumId || 'music';
      const seasonNodes = this.getSeasonNodes(curriculumId, seasonId);

      // Determine which nodes should be unlocked based on completed levels
      const shouldBeUnlocked = new Set(seasonPass.unlockedNodes);

      for (const levelId of completedLevels) {
        // Find nodes that should be unlocked by this level
        const nodesToUnlock = seasonNodes.filter(node =>
          node.requirements.type === 'level_complete' &&
          node.requirements.levelId === levelId
        );

        nodesToUnlock.forEach(node => {
          shouldBeUnlocked.add(node.id);
        });
      }

      // Update if there are new unlocks
      const newUnlockedNodes = Array.from(shouldBeUnlocked);
      if (newUnlockedNodes.length !== seasonPass.unlockedNodes.length ||
          !newUnlockedNodes.every(id => seasonPass.unlockedNodes.includes(id))) {

        console.log('[SeasonPassService] Updating unlocked nodes:', newUnlockedNodes);

        const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');
        const updates: any = {};
        updates[`${this.spField(seasonId, options?.squadId, curriculumId)}.unlockedNodes`] = newUnlockedNodes;
        updates[`${this.spField(seasonId, options?.squadId, curriculumId)}.updatedAt`] = serverTimestamp();
        await updateDoc(reflectionGameRef, updates);
      }
    } catch (error) {
      console.error('Error initializing season pass from progress:', error);
    }
  }

  /**
   * Reset user's season pass (for testing purposes)
   */
  static async resetUserSeasonPass(
    userId: string,
    seasonId: SeasonId = DEFAULT_SEASON
  ): Promise<void> {
    try {
      const reflectionGameRef = doc(db, 'users', userId, 'reflectionGame', 'state');

      // Reset season pass and unlocked games
      await updateDoc(reflectionGameRef, {
        [`seasonPass.${seasonId}`]: null,
        unlockedGames: null,
        unlockedGamesLastUpdated: null
      });

      // Reinitialize
      await this.initializeUserSeasonPass(userId, seasonId);
    } catch (error) {
      console.error('Error resetting user season pass:', error);
      throw new Error('Failed to reset season pass');
    }
  }
}